import { useId } from '@react-aria/utils';
import { ReactElement } from 'react';

import { CloseButton } from '../IconButton';
import { IconAndTextWrapper, IconWrapper, ListItem, ListItemText, SimpleCheckIcon } from './styles';
import { IconListItemProps } from './types';

/**
 * An Icon List Item can be used to represent a list of options
 * with a left-hand icon displayed.
 * Optionally, a dismiss button can be shown on the right-hand side.
 * The icon container has a number of styling options.
 */
const IconListItem = ({
	Icon,
	iconBackgroundColor,
	iconBackgroundAlpha,
	iconBorderColor = 'transparent',
	noItemMargin,
	onDismiss,
	dismissButtonAriaLabel,
	dismissIcon,
	dismissIconColor,
	condensed = false,
	currentUserCheck = false,
	isInactiveListItem = false,
	children,
	...props
}: IconListItemProps): ReactElement => {
	const uniqueId = useId();

	return (
		<ListItem $noItemMargin={noItemMargin} {...props} $isInactive={isInactiveListItem}>
			<IconAndTextWrapper>
				<IconWrapper
					$iconBackgroundAlpha={iconBackgroundAlpha}
					$iconBackgroundColor={iconBackgroundColor}
					$iconBorderColor={iconBorderColor}
					$condensed={condensed}
				>
					<Icon />
				</IconWrapper>
				<ListItemText id={uniqueId} $condensed={condensed}>
					{children}
				</ListItemText>
			</IconAndTextWrapper>
			{onDismiss ? (
				<CloseButton
					color={dismissIconColor ?? 'white'}
					Icon={dismissIcon}
					aria-label={dismissButtonAriaLabel}
					onPress={onDismiss}
					aria-controls={uniqueId}
				/>
			) : currentUserCheck ? (
				<SimpleCheckIcon />
			) : null}
		</ListItem>
	);
};

export default IconListItem;
